import './WaitlistActivationView.css'

import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { useAtomValue, useSetAtom } from 'jotai'

import type { FormValidator } from '../components/FormValidators'

import { session } from '../store'
import { api } from '../lib/api'
import type { FieldError } from '../lib/api'

import { Button } from '../components/Button'
import { FieldError as FieldErrorComponent } from '../components/FieldError'
import { Form } from '../components/Form'
import { TextField } from '../components/TextField'
import { useFormFieldErrors } from '../hooks/useFormFieldErrors'
import { atom } from 'jotai'

type FormValue = {
  email: string
  password: string
  confirmPassword: string
}

const validatePasswordMatch: FormValidator<FormValue> = (value) => {
  if (value.password && value.confirmPassword && value.password !== value.confirmPassword) {
    return { confirmPassword: 'mismatch' }
  }
  return { confirmPassword: [] }
}

const conversionIsLoadingAtom = atom(false)
const conversionIssuesAtom = atom<FieldError[]>([])

export function WaitlistActivationView() {
  const [searchParams] = useSearchParams()
  const email = searchParams.get('email') ?? ''

  const [formValue, setFormValue] = useState<FormValue>({
    email,
    password: '',
    confirmPassword: '',
  })
  const isLoading = useAtomValue(conversionIsLoadingAtom)
  const setIsLoading = useSetAtom(conversionIsLoadingAtom)
  const setIssues = useSetAtom(conversionIssuesAtom)
  const authStatus = useAtomValue(session.sessionStatusAtom)
  const establishSession = useSetAtom(session.establishSessionAtom)
  const navigate = useNavigate()

  const serverErrors = useFormFieldErrors(conversionIssuesAtom)

  useEffect(() => {
    if (authStatus === 'authenticated') {
      navigate('/profile', { replace: true })
    }
  }, [authStatus, navigate])

  const handleSubmit = async () => {
    if (!formValue.email) {
      setIssues([{ email: 'required' }])
      return
    }

    setIsLoading(true)
    setIssues([])

    try {
      const response = await api.fetch('/waitlist/convert', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formValue.email,
          password: formValue.password,
        }),
      })

      if (!response.ok) {
        const data = await response.json()
        setIsLoading(false)

        if (data.error === 'regionNotActive') {
          setIssues([{ email: 'regionNotActive' }])
          return
        }

        if (Array.isArray(data)) {
          setIssues(data)
        } else if (data.fields) {
          setIssues(data.fields)
        } else {
          setIssues([{ email: 'conversionFailed' }])
        }
        return
      }

      const data = await response.json()
      // Convert returns { user, token }; session shape matches that shape.
      if (!data?.token?.value || !data?.user) {
        setIsLoading(false)
        setIssues([{ email: 'conversionFailed' }])
        return
      }
      await establishSession(data)
      setIsLoading(false)
    } catch (_err) {
      setIsLoading(false)
      setIssues([{ email: 'networkError' }])
    }
  }

  if (!email) {
    return (
      <div className="waitlist-activation-view waitlist-activation-view--error">
        <h2 className="waitlist-activation-view__title">Invalid Link</h2>
        <p className="waitlist-activation-view__message">
          This activation link is invalid or has expired. Please check your email for the correct
          link, or <Link to="/register">sign up again</Link>.
        </p>
      </div>
    )
  }

  return (
    <Form
      className="waitlist-activation-view"
      value={formValue}
      onChange={setFormValue}
      onSubmit={handleSubmit}
      flags={serverErrors}
      validate={validatePasswordMatch}
    >
      <h2 className="waitlist-activation-view__title">Complete Your Application</h2>

      <p className="waitlist-activation-view__info">
        Great news! Your area is now active. Create a password to complete your application.
      </p>

      <TextField name="email" label="Email" disabled />

      <FieldErrorComponent
        name="email"
        text={{
          required: 'Email is missing from the activation link',
          notOnWaitlist: "This email isn't on our waitlist. Try registering instead.",
          regionNotActive: "Your region isn't active yet. We'll notify you when it is!",
          emailAlreadyInUse: 'This email is already registered. Try logging in instead.',
          conversionFailed: 'Something went wrong. Please try again.',
          networkError: 'Network error. Please try again.',
        }}
      />

      <TextField
        name="password"
        label="Password"
        kind="password"
        placeholder="Create a password"
        required
        min={8}
        disabled={isLoading}
      />
      <FieldErrorComponent
        name="password"
        text={{
          required: 'Password is required',
          min: 'Password must be at least 8 characters',
          tooShort: 'Password must be at least 8 characters',
          tooLong: 'Password is too long',
        }}
      />

      <TextField
        name="confirmPassword"
        label="Confirm Password"
        kind="password"
        placeholder="Confirm your password"
        required
        disabled={isLoading}
      />
      <FieldErrorComponent
        name="confirmPassword"
        text={{
          required: 'Please confirm your password',
          mismatch: 'Passwords do not match',
        }}
      />

      <div className="waitlist-activation-view__actions">
        <Button type="submit" kind="primary" disabled={isLoading}>
          {isLoading ? 'Creating Account...' : 'Create Account'}
        </Button>
        <Link to="/login" className="waitlist-activation-view__alt-action">
          Already have an account?
        </Link>
      </div>
    </Form>
  )
}
